/**
 * Downloading an artifact.
 *
 * On a PII project the API demands a reason, so the button asks for one rather than
 * letting the request fail and showing an error the user has to decode.
 */

import { useState } from "react";

import { ApiError } from "@/api/client";
import type { Artifact } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { Notice } from "./ui";

export function DownloadButton({
  projectSlug,
  runId,
  artifact,
  needsReason,
  blocked = false,
}: {
  projectSlug: string;
  runId: string;
  artifact: Artifact;
  needsReason: boolean;
  /** The run failed validation. An admin may still release it, deliberately. */
  blocked?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  if (artifact.purged_at) {
    return (
      <span className="faint" title="Deleted by the project's retention policy">
        Expired
      </span>
    );
  }

  const download = async (withReason?: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      const result = await endpoints.downloadArtifact(
        projectSlug,
        runId,
        artifact.id,
        withReason,
      );
      // A presigned URL is a capability over one object; navigating is enough and keeps
      // the file out of the page's memory.
      window.location.href = result.url;
      setAsking(false);
      setReason("");
    } catch (caught) {
      setProblem(
        caught instanceof ApiError ? caught.message : (caught as Error).message,
      );
      if (caught instanceof ApiError && caught.status === 422) setAsking(true);
    } finally {
      setBusy(false);
    }
  };

  // A blocked output is always a deliberate act, so it always asks — even on a project
  // that would not otherwise need a reason.
  const mustExplain = needsReason || blocked;

  if (asking) {
    return (
      <div className="stack" style={{ gap: 6, minWidth: 260 }}>
        {blocked && (
          <div className="field__hint">
            This output failed validation. Releasing it is recorded against your name.
          </div>
        )}
        <input
          className="input"
          placeholder={blocked ? "Why is it safe to release this?" : "Why do you need this file?"}
          value={reason}
          autoFocus
          onChange={(event) => setReason(event.target.value)}
        />
        {problem && <div className="field__error">{problem}</div>}
        <div className="row">
          <button
            type="button"
            className={`button button--small ${blocked ? "button--danger" : "button--primary"}`}
            disabled={busy || reason.trim().length < 10}
            onClick={() => download(reason)}
          >
            {blocked ? "Release anyway" : "Download"}
          </button>
          <button
            type="button"
            className="button button--small button--ghost"
            onClick={() => setAsking(false)}
          >
            Cancel
          </button>
        </div>
        {reason.trim().length < 10 && (
          <div className="faint" style={{ fontSize: 12 }}>
            At least 10 characters. Recorded against your name.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <button
        type="button"
        className={`button button--small${blocked ? " button--danger" : ""}`}
        disabled={busy}
        onClick={() => (mustExplain ? setAsking(true) : download())}
      >
        {blocked ? "Release anyway" : "Download"}
      </button>
      {problem && <Notice tone="danger">{problem}</Notice>}
    </div>
  );
}
